import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Youtube, Plus, Edit3, Trash2, Save, X, Loader2, Eye, EyeOff,
  ArrowUp, ArrowDown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

const blank = {
  name: "",
  short: "",
  channel_id: "",
  color: "#6CABDD",
  official: false,
  active: true,
};

export default function AdminVideoChannels() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/video-channels");
      setChannels(r.data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/video-channels/${c.id}`, { active: !c.active });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Videos from this channel will stop appearing.`)) return;
    try {
      await api.delete(`/admin/video-channels/${c.id}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Move a channel up/down in the pill order. Optimistically reorder
  // the local list before hitting the API so the click feels instant;
  // on failure we re-fetch to restore the server's truth.
  const move = async (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= channels.length) return;
    const next = channels.slice();
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    setChannels(next);
    try {
      await api.post("/admin/video-channels/reorder", {
        ids: next.map((c) => c.id),
      });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      await load();
    }
  };

  return (
    <div
      className="admin-chrome min-h-screen bg-[#F8FAFC] text-[var(--brand-ink)]"
      data-testid="page-admin-video-channels"
    >
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <RouterLink
            to="/admin"
            className="inline-flex items-center gap-2 font-display text-sm font-semibold"
          >
            <ArrowLeft size={14} /> Admin
          </RouterLink>
          <span className="label-kicker text-[var(--brand-body)]">
            YouTube channels · {user?.name || user?.email}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
        <div className="mb-8 border-b border-black/10 pb-6">
          <div className="flex items-center gap-2 text-[#6CABDD]">
            <Youtube size={16} strokeWidth={2.5} />
            <span className="label-kicker">Video feed editor</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Choose the creators on{" "}
            <RouterLink to="/videos" className="underline decoration-[#6CABDD] underline-offset-4">
              /videos
            </RouterLink>.
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-[var(--brand-body)]">
            Add YouTube channels by their ID (starts with{" "}
            <span className="font-mono text-xs">UC…</span>). Don't know it?
            Open the channel on YouTube, click "…more" on the About tab, then
            "Share channel" → "Copy channel ID".
          </p>
        </div>

        <ChannelForm onSaved={load} setError={setError} />

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="admin-video-channels-error"
          >
            {error}
          </p>
        ) : null}

        <h2 className="mb-4 font-display text-lg font-bold">
          In rotation ({channels.length})
        </h2>

        {loading ? (
          <div className="flex items-center gap-2 text-[var(--brand-body)]">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : channels.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-black/15 bg-white p-8 text-center text-sm text-[var(--brand-body)]"
            data-testid="admin-video-channels-empty"
          >
            No channels yet. Add your first above.
          </div>
        ) : (
          <ul
            className="overflow-hidden rounded-md border border-black/10 bg-white"
            data-testid="admin-video-channels-list"
          >
            {channels.map((c, idx) => (
              <li
                key={c.id}
                className="flex flex-col gap-3 border-b border-black/5 px-5 py-4 last:border-b-0 sm:flex-row sm:items-start sm:justify-between"
              >
                {/* Order arrows — tucked left of the channel name so the
                    primary actions on the right stay where admins expect. */}
                {editing?.id !== c.id ? (
                  <div className="flex shrink-0 items-start gap-1 pt-0.5">
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      data-testid={`admin-channel-move-up-${c.id}`}
                      title="Move up"
                      className="rounded p-1.5 text-[var(--brand-body)] hover:bg-black/5 hover:text-[var(--brand-ink)] disabled:opacity-30"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(idx, +1)}
                      disabled={idx === channels.length - 1}
                      data-testid={`admin-channel-move-down-${c.id}`}
                      title="Move down"
                      className="rounded p-1.5 text-[var(--brand-body)] hover:bg-black/5 hover:text-[var(--brand-ink)] disabled:opacity-30"
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>
                ) : null}
                <div className="min-w-0 flex-1">
                  {editing?.id === c.id ? (
                    <ChannelInlineEdit
                      ch={c}
                      onCancel={() => setEditing(null)}
                      onSaved={async () => {
                        setEditing(null);
                        await load();
                      }}
                      setError={setError}
                    />
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ background: c.color }}
                        />
                        <p className="truncate font-display text-base font-bold">
                          {c.name}
                          <span className="ml-2 label-kicker text-[var(--brand-body)]">
                            · {c.short}
                          </span>
                          {c.official ? (
                            <span className="ml-2 rounded-sm bg-[#6CABDD]/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--brand-ink)]">
                              official
                            </span>
                          ) : null}
                          {!c.active ? (
                            <span className="ml-2 rounded-sm bg-[#475569]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--brand-body)]">
                              off
                            </span>
                          ) : null}
                        </p>
                      </div>
                      <a
                        href={`https://www.youtube.com/channel/${c.channel_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 block truncate font-mono text-xs text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
                      >
                        {c.channel_id}
                      </a>
                    </>
                  )}
                </div>
                {editing?.id !== c.id ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => toggleActive(c)}
                      data-testid={`admin-channel-toggle-${c.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-1.5 label-kicker hover:border-[#0A0F17]"
                    >
                      {c.active ? <EyeOff size={12} /> : <Eye size={12} />}
                      {c.active ? "Hide" : "Show"}
                    </button>
                    <button
                      onClick={() => setEditing(c)}
                      data-testid={`admin-channel-edit-${c.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-1.5 label-kicker hover:border-[#0A0F17]"
                    >
                      <Edit3 size={12} /> Edit
                    </button>
                    <button
                      onClick={() => remove(c)}
                      data-testid={`admin-channel-delete-${c.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-[#E63946] px-3 py-1.5 label-kicker text-[#E63946] hover:bg-[#E63946] hover:text-white"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function ChannelForm({ onSaved, setError }) {
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => {
    const v = k === "active" || k === "official" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const cid = form.channel_id.trim();
    if (!cid.startsWith("UC") || cid.length < 20) {
      setError('Channel ID should start with "UC" and be ~24 characters long.');
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/video-channels", {
        name: form.name.trim(),
        short: form.short.trim(),
        channel_id: cid,
        color: form.color,
        official: form.official,
        active: form.active,
      });
      setForm(blank);
      await onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mb-10 rounded-md border border-black/10 bg-white p-6"
      data-testid="admin-video-channel-form"
    >
      <h2 className="font-display text-lg font-bold">Add a YouTube channel</h2>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Labeled label="Name">
          <input
            required
            value={form.name}
            onChange={set("name")}
            data-testid="admin-channel-name"
            placeholder="e.g. Coventry City Official"
            className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
          />
        </Labeled>
        <Labeled label="Short label (for pills)">
          <input
            required
            value={form.short}
            onChange={set("short")}
            data-testid="admin-channel-short"
            placeholder="Official"
            className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
          />
        </Labeled>
      </div>
      <Labeled label="YouTube channel ID (starts with UC…)" className="mt-4">
        <input
          required
          value={form.channel_id}
          onChange={set("channel_id")}
          data-testid="admin-channel-id"
          placeholder="UCch_NWdo3JWKngAyO9XlycA"
          className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17]"
        />
      </Labeled>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Labeled label="Accent colour">
          <input
            type="color"
            value={form.color}
            onChange={set("color")}
            data-testid="admin-channel-color"
            className="h-10 w-full rounded-md border border-black/15 bg-white px-1 py-1 outline-none"
          />
        </Labeled>
        <label className="flex items-end gap-2 pb-1">
          <input
            type="checkbox"
            checked={form.official}
            onChange={set("official")}
            data-testid="admin-channel-official"
            className="h-4 w-4 rounded border-black/20"
          />
          <span className="text-sm">Official club channel</span>
        </label>
        <label className="flex items-end gap-2 pb-1">
          <input
            type="checkbox"
            checked={form.active}
            onChange={set("active")}
            data-testid="admin-channel-active"
            className="h-4 w-4 rounded border-black/20"
          />
          <span className="text-sm">Active immediately</span>
        </label>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          type="submit"
          disabled={saving}
          data-testid="admin-channel-save"
          className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2.5 font-display text-sm font-bold uppercase tracking-wide text-white disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {saving ? "Adding…" : "Add channel"}
        </button>
      </div>
    </form>
  );
}

function ChannelInlineEdit({ ch, onCancel, onSaved, setError }) {
  const [form, setForm] = useState({
    name: ch.name,
    short: ch.short,
    channel_id: ch.channel_id,
    color: ch.color || "#6CABDD",
    official: !!ch.official,
  });
  const [saving, setSaving] = useState(false);

  const setStr = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      await api.put(`/admin/video-channels/${ch.id}`, {
        name: form.name.trim(),
        short: form.short.trim(),
        channel_id: form.channel_id.trim(),
        color: form.color,
        official: form.official,
      });
      await onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full space-y-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <input value={form.name} onChange={setStr("name")} className="rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]" />
        <input value={form.short} onChange={setStr("short")} className="rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]" />
      </div>
      <input value={form.channel_id} onChange={setStr("channel_id")} className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17]" />
      <div className="flex flex-wrap items-center gap-3">
        <input type="color" value={form.color} onChange={setStr("color")} className="h-10 w-14 rounded-md border border-black/15 bg-white px-1 py-1 outline-none" />
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.official} onChange={setBool("official")} className="h-4 w-4" />
          Official
        </label>
      </div>
      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-3 py-1.5 label-kicker text-white"
        >
          <Save size={12} /> Save
        </button>
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-1.5 label-kicker"
        >
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
}

function Labeled({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label-kicker text-[var(--brand-body)]">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
